import * as React from "react";
import { Wifi, WifiOff } from "lucide-react";

/**
 * ConnectionPill — top-right subtle indicator for the SSE connection.
 *
 * `connected: true`   → tiny green dot with "Live" tooltip (fades in/out).
 * `connected: false`  → soft amber pill "Reconnecting…" (auto-reconnect
 *                       handled inside useCompanyEvents).
 *
 * Placed inside AppShell fixed on the right side of the top-bar area
 * (never a full-screen "dead" state; drop the pill on reconnect success).
 */
export default function ConnectionPill({ connected, testid = "connection-pill" }) {
  const [showReconnect, setShowReconnect] = React.useState(false);

  React.useEffect(() => {
    if (connected) { setShowReconnect(false); return; }
    // Delay the reconnect pill to avoid flicker on brief drops.
    const t = setTimeout(() => setShowReconnect(true), 1600);
    return () => clearTimeout(t);
  }, [connected]);

  if (connected) {
    return (
      <div
        className="fixed top-3 right-3 z-40 pointer-events-none hidden md:flex items-center gap-1.5 opacity-90"
        aria-live="polite"
        data-testid={testid}
      >
        <span className="inline-flex items-center gap-1.5 rounded-full bg-card border border-brand-rule px-2 py-0.5 shadow-sm">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.14)]" />
          <span className="mono-num text-[10px] uppercase tracking-wider text-brand-muted">Live</span>
        </span>
      </div>
    );
  }
  if (!showReconnect) return null;
  return (
    <div
      className="fixed top-3 right-3 z-40 pointer-events-none flex items-center gap-1.5"
      aria-live="polite"
      data-testid={`${testid}-reconnecting`}
    >
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-300 px-2 py-0.5 shadow-sm text-amber-800">
        <WifiOff size={10} />
        <span className="mono-num text-[10px] uppercase tracking-wider">Reconnecting…</span>
      </span>
    </div>
  );
}

export { WifiOff, Wifi };
